import React from 'react';
import { searchResultId } from '../../utils/mindmap/globalSearch';

const MapSearchPanel = ({
  query,
  results,
  activeIndex,
  isDarkMode,
  onActiveIndexChange,
  onSelectResult,
  currentMapId,
  global = false,
  loading = false,
  error = '',
  incomplete = false,
  indexing = false,
  hasMore = false,
  onLoadMore,
}) => {
  const foreground = isDarkMode ? '#f7f7f8' : '#171719';
  const surface = isDarkMode ? 'rgba(39, 39, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)';
  const border = isDarkMode ? '#52525b' : '#d4d4d8';

  return (
    <div
      id="map-search-results"
      role="dialog"
      aria-label={global ? "Search all maps results" : "Search map results"}
      data-export-ignore="true"
      data-viewport-occlusion="true"
      onPointerDown={(event) => {
        // Keep panel interaction out of the canvas gesture system. Focus may
        // move to a result button so keyboard and touch scrolling still work.
        event.stopPropagation();
      }}
      style={{
        position: 'absolute', top: 'calc(100% + 8px)', right: 0,
        width: 'min(420px, calc(100vw - 24px))', zIndex: 220, color: foreground,
        background: surface, border: `1px solid ${border}`, borderRadius: 12,
        boxShadow: '0 16px 50px rgba(0, 0, 0, 0.28)', overflow: 'hidden',
      }}
    >
      {query && (
        <div
          role="listbox"
          aria-label="Idea search results"
          style={{ maxHeight: 320, overflowY: 'auto' }}
        >
          {results.map((result, index) => (
            <button
              key={searchResultId(result)}
              id={searchResultId(result)}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => onActiveIndexChange(index)}
              onClick={() => onSelectResult(result.mapId ? result : result.node.id)}
              style={{
                display: 'block', width: '100%', padding: '10px 14px', border: 0,
                borderBottom: index < results.length - 1 ? `1px solid ${border}` : 0,
                background: index === activeIndex
                  ? (isDarkMode ? '#3f3f46' : '#f4f4f5')
                  : 'transparent',
                color: foreground, textAlign: 'left', cursor: 'pointer',
              }}
            >
              <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {result.label}
              </span>
              {global && result.mapTitle && <span style={{ display: 'block', fontSize: 12, opacity: 0.75 }}>
                {result.mapTitle}{result.isArchived ? ' · Archived' : ''}
                {result.mapId && result.mapId !== currentMapId ? ' · Opens in a new tab ↗' : ' · This map'}
              </span>}
              {result.snippet && result.snippet !== result.label && <span style={{ display: 'block', fontSize: 12, opacity: 0.8 }}>
                {result.kind === 'document' ? 'Markdown · ' : ''}{result.snippet}
              </span>}
              {result.node.linkUrl && (
                <span style={{ display: 'block', fontSize: 12, opacity: 0.65, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {result.node.linkUrl}
                </span>
              )}
            </button>
          ))}
          {!results.length && !loading && <div style={{ padding: 14, opacity: 0.7 }}>{hasMore ? 'No matches in the maps searched so far' : 'No matching ideas'}</div>}
        </div>
      )}
      {!query && (
        <div style={{ padding: '12px 14px', opacity: 0.7 }}>
          {global ? 'Search your maps, ideas, notes and Markdown documents' : 'Type to search every idea'}
        </div>
      )}
      {global && query.trim().length === 1 && <div style={{ padding: 12 }}>Type at least two characters to search other maps and documents.</div>}
      {loading && <div role="status" style={{ padding: 12 }}>Searching your maps…</div>}
      {error && <div role="alert" style={{ padding: 12 }}>{error}</div>}
      {incomplete && <div role="status" style={{ padding: 12 }}>Some maps or documents could not be searched. Try again later.</div>}
      {indexing && !incomplete && <div role="status" style={{ padding: 12 }}>Some recent changes are still becoming searchable. Try again shortly.</div>}
      {hasMore && <button type="button" disabled={loading} onClick={onLoadMore}
        style={{ padding: 12, width: '100%', color: foreground, background: surface, border: 0, cursor: 'pointer' }}>
        Search more maps and ideas
      </button>}
      <div style={{
        padding: '6px 12px',
        borderTop: `1px solid ${border}`,
        fontSize: 11,
        opacity: 0.65,
      }}>
        ↑↓ navigate · Enter reveal · Esc dismiss
      </div>
    </div>
  );
};

export default MapSearchPanel;
